import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import * as Icons from "lucide-react";
import { Plus, ArrowLeft, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  open: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  closed: "bg-emerald-100 text-emerald-800",
  archived: "bg-zinc-200 text-zinc-600",
};
const PRIORITY_COLOR = {
  low: "text-muted-foreground",
  medium: "text-primary",
  high: "text-orange-600",
  critical: "text-destructive font-semibold",
};

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  useEffect(() => {
    api.get(`/module-config/${moduleId}`).then((r) => {
      setCfg(r.data);
      setActiveType(r.data.record_types[0].id);
    }).catch(() => {
      toast.error("Module not found");
      nav("/app/modules");
    });
  }, [moduleId, nav]);

  const load = () => {
    if (!activeType) return;
    api.get(`/records?module_id=${moduleId}&record_type=${activeType}`)
      .then((r) => setRecords(r.data));
  };
  useEffect(load, [moduleId, activeType]);

  const filtered = records.filter((r) =>
    r.title.toLowerCase().includes(q.toLowerCase())
  );

  const create = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const { data } = await api.post("/records", {
        module_id: moduleId,
        record_type: activeType,
        title: newTitle,
      });
      toast.success("Created");
      setCreating(false);
      setNewTitle("");
      nav(`/app/records/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/records/${id}`);
    toast.success("Deleted");
    load();
  };

  if (!cfg) return <div className="p-8 font-mono-plex text-sm">Loading module…</div>;

  const Icon = Icons[cfg.icon] || Icons.Box;
  const currentType = cfg.record_types.find((t) => t.id === activeType);

  return (
    <div className="p-8 space-y-6" data-testid="module-detail-page">
      <div>
        <Link to="/app/modules" className="inline-flex items-center gap-1 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-modules">
          <ArrowLeft className="w-3 h-3" /> Back to modules
        </Link>
        <div className="mt-3 flex items-center gap-3">
          <Icon className="w-7 h-7 text-primary" />
          <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="module-name">
            {cfg.name}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mt-2 font-mono-plex uppercase tracking-widest">
          / {cfg.record_types.length} record types · {records.length} in view
        </p>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="rounded-sm border border-border bg-card p-1 h-auto" data-testid="record-type-tabs">
          {cfg.record_types.map((t) => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              data-testid={`tab-${t.id}`}
              className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-plex uppercase text-xs tracking-widest"
            >
              {t.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {cfg.record_types.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-6 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 bg-card flex-1 max-w-sm">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${t.name.toLowerCase()}…`}
                  data-testid={`search-${t.id}`}
                  className="border-0 focus-visible:ring-0 h-8 p-0"
                />
              </div>
              <Dialog open={creating} onOpenChange={setCreating}>
                <DialogTrigger asChild>
                  <Button data-testid={`new-${t.id}-btn`} className="rounded-sm bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 mr-2" /> New {t.singular}
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm" data-testid="new-record-dialog">
                  <form onSubmit={create}>
                    <DialogHeader>
                      <DialogTitle className="font-display">New {t.singular}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <Label>Title</Label>
                      <Input
                        autoFocus required data-testid="new-record-title"
                        className="rounded-sm mt-1.5"
                        placeholder={`e.g., ${t.singular} for Line 3`}
                        value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                      />
                      {t.ai && (
                        <p className="text-xs text-muted-foreground mt-3 font-mono-plex uppercase tracking-widest">
                          ◉ AI drafting available after creation
                        </p>
                      )}
                    </div>
                    <DialogFooter>
                      <Button type="submit" data-testid="new-record-submit" className="rounded-sm bg-primary hover:bg-primary/90">
                        Create
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {filtered.length === 0 ? (
              <div className="border border-dashed border-border p-16 text-center bg-card" data-testid={`empty-${t.id}`}>
                <Icon className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="mt-4 font-display text-lg">No {t.name.toLowerCase()} yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first {t.singular.toLowerCase()}.</p>
              </div>
            ) : (
              <div className="border border-border bg-card">
                <table className="w-full text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="px-4 py-3 overline text-muted-foreground">Title</th>
                      <th className="px-4 py-3 overline text-muted-foreground">Status</th>
                      <th className="px-4 py-3 overline text-muted-foreground">Priority</th>
                      <th className="px-4 py-3 overline text-muted-foreground">Assignee</th>
                      <th className="px-4 py-3 overline text-muted-foreground">Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} onClick={() => nav(`/app/records/${r.id}`)} className="border-b border-border hover:bg-secondary/50 cursor-pointer" data-testid={`record-row-${r.id}`}>
                        <td className="px-4 py-3 font-medium">{r.title}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${STATUS_COLOR[r.status] || STATUS_COLOR.open} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 font-mono-plex text-xs uppercase tracking-widest ${PRIORITY_COLOR[r.priority]}`}>
                          {r.priority}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{r.assignee || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(r.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => del(r.id, e)} data-testid={`delete-${r.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
